/**
 * Modal that captures a name + description + scope for a new ReportTemplate
 * built from the current builder config. The owning ReportBuilder hands in
 * the chart_type and config snapshot to persist.
 */

import { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import {
  reportingAPI,
  type IndicatorBuilderConfig,
} from '../../../lib/api/reporting';
import type { BuilderChartType } from './ChartTypePicker';

interface SaveAsTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  chartType: BuilderChartType;
  config: IndicatorBuilderConfig;
  defaultName?: string;
}

export function SaveAsTemplateDialog({
  open,
  onClose,
  chartType,
  config,
  defaultName,
}: SaveAsTemplateDialogProps) {
  const [name, setName] = useState(defaultName || '');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<'user' | 'company'>('user');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName(defaultName || '');
    setDescription('');
    setScope('user');
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Template needs a name');
      return;
    }
    setSaving(true);
    try {
      await reportingAPI.createTemplate({
        name: name.trim(),
        description: description.trim(),
        scope,
        chart_type: chartType,
        config: config as Record<string, unknown>,
      });
      toast.success('Template saved');
      reset();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save template';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save as Template</DialogTitle>
          <DialogDescription>
            Reuse this configuration when creating future reports.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="template-name" className="text-xs">Name</Label>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Monthly Top Suppliers"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="template-description" className="text-xs">Description</Label>
            <Textarea
              id="template-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this report show? When should someone use it?"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Visibility</Label>
            <RadioGroup
              value={scope}
              onValueChange={(v: string) => setScope(v as 'user' | 'company')}
              className="space-y-1"
            >
              <div className="flex items-start gap-2">
                <RadioGroupItem value="user" id="scope-user" className="mt-0.5" />
                <Label htmlFor="scope-user" className="text-xs font-normal cursor-pointer">
                  <span className="font-medium">Just me</span>
                  <span className="block text-muted-foreground">
                    Only you can see and apply this template.
                  </span>
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="company" id="scope-company" className="mt-0.5" />
                <Label htmlFor="scope-company" className="text-xs font-normal cursor-pointer">
                  <span className="font-medium">My company</span>
                  <span className="block text-muted-foreground">
                    Everyone in your company can apply this template.
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Template'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SaveAsTemplateDialog;
